import axios from 'axios';
import { getIotUrl, getBaseURL } from '../../../api/apiManagement';

const DMS_BASE_URL = () => {
  const iotUrl = getIotUrl();
  return `${iotUrl}/api`;
};

// File Upload API
export const uploadFiles = async (files) => {
  try {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    const response = await axios.post(
      `${DMS_BASE_URL()}/dms/documents/upload/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Document CRUD APIs
export const createDocument = async (documentData) => {
  try {
    const response = await axios.post(
      `${DMS_BASE_URL()}/documents/`,
      documentData
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getDocuments = async () => {
  try {
    const response = await axios.get(`${DMS_BASE_URL()}/documents/`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const filterDocuments = async (filters) => {
  try {
    const response = await axios.post(
      `${DMS_BASE_URL()}/documents/filter/`,
      filters
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getDocumentDetail = async (recordId) => {
  try {
    const response = await axios.get(
      `${DMS_BASE_URL()}/documents/${recordId}/`
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const updateDocument = async (recordId, documentData) => {
  try {
    const response = await axios.put(
      `${DMS_BASE_URL()}/documents/${recordId}/`,
      documentData
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const deleteDocument = async (recordId) => {
  try {
    const response = await axios.delete(
      `${DMS_BASE_URL()}/documents/${recordId}/`
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Assignment APIs
export const assignDocument = async (recordId, assignmentData) => {
  try {
    const response = await axios.post(
      `${DMS_BASE_URL()}/dms/documents/${recordId}/assign/`,
      assignmentData
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getAssignments = async () => {
  try {
    const response = await axios.get(`${DMS_BASE_URL()}/dms/document-assignments/`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const filterAssignments = async (filters) => {
  try {
    const response = await axios.post(
      `${DMS_BASE_URL()}/dms/document-assignments/filter/`,
      filters
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getAssignmentDetail = async (assignmentId) => {
  try {
    const response = await axios.get(
      `${DMS_BASE_URL()}/dms/document-assignments/${assignmentId}/`
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const updateAssignment = async (assignmentId, assignmentData) => {
  try {
    const response = await axios.put(
      `${DMS_BASE_URL()}/dms/document-assignments/${assignmentId}/`,
      assignmentData
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const updateAssignmentStatus = async (assignmentId, status, notes = '', updatedBy = null) => {
  try {
    const userId = updatedBy || localStorage.getItem('userId') || sessionStorage.getItem('userId');
    const response = await axios.put(
      `${DMS_BASE_URL()}/dms/document-assignments/${assignmentId}/status/`,
      {
        status: status,
        notes: notes,
        updated_by: userId,
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Update assignment status using record_id
export const updateAssignmentStatusByRecordId = async (recordId, status, notes = '', updatedBy = null) => {
  try {
    const userId = updatedBy || localStorage.getItem('userId') || sessionStorage.getItem('userId');
    const response = await axios.put(
      `${DMS_BASE_URL()}/dms/document-assignments/${recordId}/status/`,
      {
        status: status,
        notes: notes,
        updated_by: userId,
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const deleteAssignment = async (assignmentId) => {
  try {
    const response = await axios.delete(
      `${DMS_BASE_URL()}/dms/document-assignments/${assignmentId}/`
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Activity Log API
export const filterActivityLogs = async (filters) => {
  try {
    const response = await axios.post(
      `${DMS_BASE_URL()}/dms/activity/logs/filter/`,
      filters
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Dashboard Summary API
export const getDashboardSummary = async (filters) => {
  try {
    const response = await axios.post(
      `${DMS_BASE_URL()}/dms/dashboard/summary/`,
      filters || {}
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Get Users/Employees API
export const getUsers = async (companyName = 'pwc') => {
  try {
    const baseURL = getBaseURL();
    const response = await axios.get(`${DMS_BASE_URL()}/users`, {
      params: {
        company_name: companyName,
      },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Notification APIs
export const getNotifications = async (userId) => {
  try {
    const response = await axios.get(
      `${DMS_BASE_URL()}/notifications/${userId}/`,
      {
        headers: {
          'accept': 'application/json',
        },
      }
    );
    return response.data;
  } catch (error) {
    // Graceful degradation - return empty array if API fails
    console.error('Error fetching notifications:', error);
    return [];
  }
};

export const markNotificationAsRead = async (notificationId) => {
  try {
    const response = await axios.put(
      `${DMS_BASE_URL()}/notifications/${notificationId}/read/`
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};
